import { useEffect, useState } from "react";
import { api, API, token } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, hasRole, formatErr, dateFmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Loader2, Save, Settings, Building2, Link2, RefreshCw, Users, Plug,
    AlertTriangle, Pencil, Store, BookOpen, GraduationCap, ShieldCheck,
    FileText, Upload, Trash2, Mail,
} from "lucide-react";
import { MfaSecurityCard } from "@/components/MfaSecurityCard";

const ASSIGNABLE = [
    { v: "school_admin", l: "School Admin" },
    { v: "purchasing_admin", l: "Purchasing Admin" },
    { v: "approver", l: "Approver" },
    { v: "requester", l: "Requester" },
];

const Spin = () => <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>;
const Card = ({ title, icon: Icon, children }) => (
    <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-brand-emerald font-semibold"><Icon className="h-4 w-4 text-brand-teal" /> {title}</div>
        <div className="p-5">{children}</div>
    </div>
);
const Mocked = ({ children }) => (
    <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-4 text-sm flex items-start gap-2" data-testid="mocked-banner">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{children}</span>
    </div>
);

/* ---------------- School Profile ---------------- */
function ProfileTab() {
    const [t, setT] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/tenant").then((r) => setT(r.data)).catch(() => setT({})); }, []);
    if (!t) return <Spin />;
    const set = (k, v) => setT((s) => ({ ...s, [k]: v }));
    const save = async () => {
        setBusy(true);
        try {
            await api.put("/tenant/profile", {
                legal_name: t.legal_name, contact_email: t.contact_email, phone: t.phone,
                address_line1: t.address_line1, address_line2: t.address_line2,
                city: t.city, state: t.state, zip_code: t.zip_code,
            });
            toast.success("School profile saved");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const F = ({ k, label, ...p }) => (
        <div><Label className="text-xs">{label}</Label><Input value={t[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`profile-${k}`} {...p} /></div>
    );
    return (
        <Card title="School Profile" icon={Building2}>
            <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">School name</Label><Input value={t.name || ""} disabled className="mt-1 bg-secondary/40" data-testid="profile-name" /></div>
                <F k="legal_name" label="Legal name" />
                <F k="contact_email" label="Contact email" type="email" />
                <F k="phone" label="Phone" placeholder="(617) 555-0123" />
                <div className="sm:col-span-2"><F k="address_line1" label="Address line 1" /></div>
                <div className="sm:col-span-2"><F k="address_line2" label="Address line 2" /></div>
                <F k="city" label="City" />
                <div className="grid grid-cols-2 gap-4">
                    <F k="state" label="State" maxLength={2} placeholder="MA" />
                    <F k="zip_code" label="ZIP code" placeholder="02116" />
                </div>
            </div>
            <div className="mt-5 flex justify-end">
                <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="save-profile-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save profile</>}
                </Button>
            </div>
        </Card>
    );
}

/* ---------------- Veracross setup ---------------- */
function VeracrossTab() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ school_route: "", client_id: "", client_secret: "", scopes: "", mode: "sandbox" });
    const [busy, setBusy] = useState("");
    const load = () => api.get("/tenant/veracross").then((r) => {
        setCfg(r.data);
        setForm((f) => ({ ...f, school_route: r.data.school_route || "", client_id: r.data.client_id || "", scopes: r.data.scopes || "", mode: r.data.mode || "sandbox" }));
    });
    useEffect(() => { load().catch(() => setCfg({})); }, []);
    if (!cfg) return <Spin />;
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const save = async () => {
        if (!form.school_route || !form.client_id) { toast.error("School route and client ID are required"); return; }
        setBusy("save");
        try { await api.put("/tenant/veracross", form); toast.success("Veracross settings saved"); set("client_secret", ""); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const test = async () => {
        setBusy("test");
        try { const { data } = await api.post("/tenant/veracross/test"); toast.success(data.message); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    return (
        <div className="space-y-4">
            <Mocked>Live Veracross integration is not yet enabled. Settings are stored and all calls are <b>simulated (MOCKED)</b> until the real Data API v3 + OAuth is wired up.</Mocked>
            <Card title="Veracross Setup" icon={Link2}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs">School route</Label><Input value={form.school_route} onChange={(e) => set("school_route", e.target.value)} className="mt-1 font-mono" placeholder="stjude" data-testid="vx-route" /></div>
                    <div><Label className="text-xs">Mode</Label>
                        <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                            <SelectTrigger className="mt-1" data-testid="vx-mode"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
                        </Select>
                    </div>
                    <div><Label className="text-xs">Client ID</Label><Input value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="mt-1 font-mono" data-testid="vx-client-id" /></div>
                    <div><Label className="text-xs">Client secret</Label><Input type="password" value={form.client_secret} onChange={(e) => set("client_secret", e.target.value)} className="mt-1 font-mono" placeholder={cfg.has_secret ? "•••••••• (saved — leave blank to keep)" : "Enter client secret"} data-testid="vx-client-secret" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Scopes</Label><Input value={form.scopes} onChange={(e) => set("scopes", e.target.value)} className="mt-1 font-mono text-xs" placeholder="ap_invoices:create ap_invoice_items:create vendors:list gl_accounts:list" data-testid="vx-scopes" /></div>
                </div>
                <div className="mt-5 flex flex-wrap justify-between gap-3">
                    <Button disabled={busy} variant="outline" onClick={test} className="rounded-lg" data-testid="vx-test-button">
                        {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plug className="h-4 w-4 mr-1" /> Test connection</>}
                    </Button>
                    <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="vx-save-button">
                        {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save settings</>}
                    </Button>
                </div>
            </Card>
        </div>
    );
}

/* ---------------- Sync ---------------- */
function SyncTab() {
    const [counts, setCounts] = useState({ vendors: null, gl: null, staff: null });
    const [status, setStatus] = useState({});
    const [busy, setBusy] = useState("");
    const load = async () => {
        const [v, g, u, s] = await Promise.all([
            api.get("/vendors"), api.get("/gl-accounts"), api.get("/users"), api.get("/sync/status"),
        ]);
        setCounts({ vendors: v.data.length, gl: g.data.length, staff: u.data.filter((x) => x.is_staff).length });
        setStatus(s.data || {});
    };
    useEffect(() => { load().catch(() => {}); }, []);
    const sync = async (kind, url) => {
        setBusy(kind);
        try { const { data } = await api.post(url); toast.success(`Synced — ${data.added} added, ${data.updated} updated (MOCKED)`); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const items = [
        { kind: "vendors", sk: "vendors", label: "Vendors", icon: Store, count: counts.vendors, url: "/sync/vendors" },
        { kind: "gl", sk: "gl_accounts", label: "GL Accounts", icon: BookOpen, count: counts.gl, url: "/sync/gl-accounts" },
        { kind: "faculty", sk: "faculty", label: "Faculty & Staff", icon: GraduationCap, count: counts.staff, url: "/sync/faculty" },
    ];
    return (
        <div className="space-y-4">
            <Mocked>Sync pulls reference data as if from Veracross. Results are <b>simulated (MOCKED)</b>.</Mocked>
            <div className="grid sm:grid-cols-3 gap-4">
                {items.map((it) => {
                    const Icon = it.icon;
                    const st = status[it.sk];
                    return (
                        <div key={it.kind} className="bg-white border border-border rounded-xl p-5" data-testid={`sync-card-${it.kind}`}>
                            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{it.label}</span><Icon className="h-4 w-4 text-brand-teal" /></div>
                            <div className="mt-3 text-3xl font-semibold text-brand-emerald" data-testid={`sync-count-${it.kind}`}>{it.count == null ? "—" : it.count}</div>
                            {st ? (
                                <div className="mt-2 text-[11px] text-muted-foreground leading-relaxed" data-testid={`sync-meta-${it.kind}`}>
                                    Last synced {dateFmt(st.at)}<br />+{st.added} added · {st.updated} updated
                                </div>
                            ) : (
                                <div className="mt-2 text-[11px] text-muted-foreground">Never synced</div>
                            )}
                            <Button disabled={busy} variant="outline" onClick={() => sync(it.kind, it.url)} className="mt-4 w-full rounded-lg" data-testid={`sync-button-${it.kind}`}>
                                {busy === it.kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Sync</>}
                            </Button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------- Users & Roles ---------------- */
function UsersTab({ isAdmin }) {
    const [users, setUsers] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [edit, setEdit] = useState(null);
    const [roles, setRoles] = useState([]);
    const [approver, setApprover] = useState("none");
    const [busy, setBusy] = useState(false);
    const load = () => Promise.all([api.get("/users"), api.get("/approvers")]).then(([u, a]) => { setUsers(u.data); setApprovers(a.data); });
    useEffect(() => { load().catch(() => setUsers([])); }, []);
    if (!users) return <Spin />;
    const nameOf = (id) => users.find((u) => u.id === id)?.name || "—";
    const openEdit = (u) => { setEdit(u); setRoles(u.roles || []); setApprover(u.initial_approver_id || "none"); };
    const toggle = (r) => setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
    const save = async () => {
        if (isAdmin && roles.length === 0) { toast.error("Select at least one role"); return; }
        setBusy(true);
        try {
            if (isAdmin) await api.put(`/users/${edit.id}/roles`, { roles });
            await api.put(`/users/${edit.id}/approver`, { approver_id: approver === "none" ? null : approver });
            toast.success("User updated"); setEdit(null); load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-brand-emerald font-semibold"><Users className="h-4 w-4 text-brand-teal" /> Users & Roles</div>
            <div className="overflow-x-auto pe-scroll">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                        <th className="p-3 font-medium">Name</th><th className="p-3 font-medium">Email</th>
                        <th className="p-3 font-medium">Roles</th><th className="p-3 font-medium">Initial Approver</th><th className="p-3" />
                    </tr></thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                                <td className="p-3 font-medium">{u.name}</td>
                                <td className="p-3 text-muted-foreground">{u.email}</td>
                                <td className="p-3"><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">{roleLabel(r)}</span>)}</div></td>
                                <td className="p-3 text-muted-foreground">{u.initial_approver_id ? nameOf(u.initial_approver_id) : "—"}</td>
                                <td className="p-3 text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
                <DialogContent data-testid="edit-user-dialog">
                    <DialogHeader><DialogTitle>{edit?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-5">
                        <div>
                            <Label className="text-xs">Roles {!isAdmin && <span className="text-muted-foreground">(only a School Admin can change roles)</span>}</Label>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                {ASSIGNABLE.map((r) => (
                                    <label key={r.v} className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm ${!isAdmin ? "opacity-60" : "cursor-pointer hover:bg-secondary/40"}`}>
                                        <Checkbox checked={roles.includes(r.v)} disabled={!isAdmin} onCheckedChange={() => toggle(r.v)} data-testid={`role-${r.v}`} /> {r.l}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Initial approver (routes this user's requests)</Label>
                            <Select value={approver} onValueChange={setApprover}>
                                <SelectTrigger className="mt-1" data-testid="approver-select"><SelectValue placeholder="No initial approver" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No initial approver</SelectItem>
                                    {approvers.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-user-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ---------------- Invoice Email Inbox ---------------- */
const EMAIL_PROVIDERS = [
    { v: "microsoft365", l: "Microsoft 365 (Exchange Online)" },
    { v: "google_workspace", l: "Google Workspace (Gmail)" },
    { v: "imap", l: "Other mailbox (IMAP + password)" },
];
const EMAIL_INSTRUCTIONS = {
    microsoft365: [
        "Sign in to the Microsoft Entra admin center (entra.microsoft.com) as a Global Administrator.",
        "Go to App registrations → New registration. Name it “ProcureEd Invoice Inbox”, choose single-tenant, and click Register.",
        "Copy the Application (client) ID and Directory (tenant) ID from the overview into the fields on the left.",
        "Open Certificates & secrets → New client secret. Copy the secret Value (not the Secret ID) and paste it into Client secret value.",
        "Open API permissions → Add a permission → Microsoft Graph → Application permissions → add Mail.Read, then click Grant admin consent.",
        "Recommended: ask your Exchange admin to scope the app to only the invoices mailbox using Application RBAC.",
        "Enter the mailbox to watch (e.g. invoices@yourschool.edu), Save, then click Test connection.",
    ],
    google_workspace: [
        "In Google Cloud Console (console.cloud.google.com), create/select a project and enable the Gmail API.",
        "Create a Service Account, then under Keys create a JSON key and download it.",
        "Paste the entire downloaded JSON key into the Service account JSON key box on the left.",
        "In the Google Admin console (admin.google.com) → Security → Access and data control → API controls → Domain-wide delegation, click Add new.",
        "Paste the service account’s Client ID and the scope https://www.googleapis.com/auth/gmail.readonly, then Authorize.",
        "Enter the mailbox to impersonate (e.g. invoices@yourschool.edu) as the Delegated mailbox, Save, then Test connection.",
    ],
    imap: [
        "In your mail provider, enable IMAP access for the invoices mailbox.",
        "If the account uses two-factor authentication, generate an app password to use here.",
        "Enter the IMAP server host (e.g. imap.yourprovider.com) and port (usually 993 for SSL).",
        "Enter the mailbox username (usually the full email address) and the password / app password.",
        "Save, then click Test connection.",
    ],
};

function EmailInboxTab() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({
        provider: "microsoft365", mailbox: "", folder: "Inbox",
        tenant_id: "", client_id: "", client_secret: "",
        delegated_email: "", service_account_json: "",
        host: "", port: "993", username: "", password: "",
    });
    const [busy, setBusy] = useState("");
    const load = () => api.get("/tenant/email-inbox").then((r) => {
        setCfg(r.data);
        if (r.data?.configured) setForm((f) => ({
            ...f, provider: r.data.provider || f.provider, mailbox: r.data.mailbox || "",
            folder: r.data.folder || "Inbox", tenant_id: r.data.tenant_id || "",
            client_id: r.data.client_id || "", delegated_email: r.data.delegated_email || "",
            host: r.data.host || "", port: r.data.port || "993", username: r.data.username || "",
        }));
    });
    useEffect(() => { load().catch(() => setCfg({ configured: false })); }, []);
    if (!cfg) return <Spin />;
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const save = async () => {
        if (form.provider !== "imap" && !form.mailbox) { toast.error("Enter the mailbox to watch"); return; }
        setBusy("save");
        try {
            await api.put("/tenant/email-inbox", form);
            toast.success("Email inbox settings saved");
            set("client_secret", ""); set("password", ""); set("service_account_json", "");
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const test = async () => {
        setBusy("test");
        try { const { data } = await api.post("/tenant/email-inbox/test"); toast.success(data.message); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const remove = async () => {
        setBusy("del");
        try { await api.delete("/tenant/email-inbox"); toast.success("Email inbox disconnected"); setCfg({ configured: false }); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const P = form.provider;
    return (
        <div className="space-y-4">
            <Mocked>Live mail syncing isn't enabled yet. Your settings are stored securely and the connection test &amp; email fetch are <b>simulated (MOCKED)</b> until credentials are wired to live mail. The <b>Review Email Invoices</b> area works today using a demo inbox.</Mocked>
            <Card title="Invoice Email Inbox" icon={Mail}>
                <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs">Provider</Label>
                            <Select value={form.provider} onValueChange={(v) => set("provider", v)}>
                                <SelectTrigger className="mt-1" data-testid="email-provider"><SelectValue /></SelectTrigger>
                                <SelectContent>{EMAIL_PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs">Folder to watch</Label><Input value={form.folder} onChange={(e) => set("folder", e.target.value)} className="mt-1" placeholder="Inbox" data-testid="email-folder" /></div>
                        {P !== "imap" && <div className="sm:col-span-2"><Label className="text-xs">Mailbox to watch</Label><Input value={form.mailbox} onChange={(e) => set("mailbox", e.target.value)} className="mt-1" placeholder="invoices@yourschool.edu" data-testid="email-mailbox" /></div>}
                        {P === "microsoft365" && <>
                            <div><Label className="text-xs">Directory (tenant) ID</Label><Input value={form.tenant_id} onChange={(e) => set("tenant_id", e.target.value)} className="mt-1 font-mono" data-testid="email-tenant-id" /></div>
                            <div><Label className="text-xs">Application (client) ID</Label><Input value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="mt-1 font-mono" data-testid="email-client-id" /></div>
                            <div className="sm:col-span-2"><Label className="text-xs">Client secret value</Label><Input type="password" value={form.client_secret} onChange={(e) => set("client_secret", e.target.value)} className="mt-1 font-mono" placeholder={cfg.client_secret_set ? "•••• saved — leave blank to keep" : "Secret Value (not the ID)"} data-testid="email-client-secret" /></div>
                        </>}
                        {P === "google_workspace" && <>
                            <div className="sm:col-span-2"><Label className="text-xs">Delegated mailbox (impersonate)</Label><Input value={form.delegated_email} onChange={(e) => set("delegated_email", e.target.value)} className="mt-1" placeholder="invoices@yourschool.edu" data-testid="email-delegated" /></div>
                            <div className="sm:col-span-2"><Label className="text-xs">Service account JSON key</Label><Textarea rows={4} value={form.service_account_json} onChange={(e) => set("service_account_json", e.target.value)} className="mt-1 font-mono text-xs" placeholder={cfg.service_account_json_set ? "•••• saved — leave blank to keep" : "Paste the downloaded JSON key"} data-testid="email-sa-json" /></div>
                        </>}
                        {P === "imap" && <>
                            <div><Label className="text-xs">IMAP host</Label><Input value={form.host} onChange={(e) => set("host", e.target.value)} className="mt-1 font-mono" placeholder="imap.yourprovider.com" data-testid="email-host" /></div>
                            <div><Label className="text-xs">Port</Label><Input value={form.port} onChange={(e) => set("port", e.target.value)} className="mt-1 font-mono" placeholder="993" data-testid="email-port" /></div>
                            <div><Label className="text-xs">Username</Label><Input value={form.username} onChange={(e) => set("username", e.target.value)} className="mt-1 font-mono" placeholder="invoices@yourschool.edu" data-testid="email-username" /></div>
                            <div><Label className="text-xs">Password / app password</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1 font-mono" placeholder={cfg.password_set ? "•••• saved — leave blank to keep" : "Password"} data-testid="email-password" /></div>
                        </>}
                    </div>
                    <div className="flex flex-wrap justify-between gap-3">
                        <div className="flex gap-2">
                            <Button disabled={busy} variant="outline" onClick={test} className="rounded-lg" data-testid="email-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plug className="h-4 w-4 mr-1" /> Test connection</>}</Button>
                            {cfg.configured && <Button disabled={busy} variant="ghost" onClick={remove} className="text-rose-600 rounded-lg" data-testid="email-remove-button"><Trash2 className="h-4 w-4 mr-1" /> Disconnect</Button>}
                        </div>
                        <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="email-save-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save settings</>}</Button>
                    </div>
                </div>
            </Card>
            <Card title={`Setup guide — ${EMAIL_PROVIDERS.find((p) => p.v === P)?.l}`} icon={FileText}>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground" data-testid="email-setup-steps">
                    {(EMAIL_INSTRUCTIONS[P] || []).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
            </Card>
        </div>
    );
}

/* ---------------- Purchase Order branding ---------------- */
function PurchaseOrderTab() {
    const [t, setT] = useState(null);
    const [footer, setFooter] = useState("");
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [bust, setBust] = useState(Date.now());
    const load = () => api.get("/tenant").then((r) => { setT(r.data); setFooter(r.data.po_footer || ""); });
    useEffect(() => { load().catch(() => setT({})); }, []);
    if (!t) return <Spin />;
    const saveFooter = async () => {
        setBusy(true);
        try { await api.put("/tenant/po-branding", { footer }); toast.success("Purchase order footer saved"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            await api.post("/tenant/po-logo", fd);
            toast.success("Logo uploaded"); setBust(Date.now()); load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setUploading(false); }
    };
    const removeLogo = async () => {
        try { await api.delete("/tenant/po-logo"); toast.success("Logo removed"); setBust(Date.now()); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };
    const logoUrl = t.po_logo_path ? `${API}/tenant/po-logo?auth=${token()}&v=${bust}` : null;
    return (
        <Card title="Purchase Order Branding" icon={FileText}>
            <div className="space-y-6">
                <div>
                    <Label className="text-xs">School logo</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Shown at the top of every purchase order PDF. PNG, JPG, WEBP or GIF, under 3&nbsp;MB.</p>
                    <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <div className="h-20 w-40 rounded-lg border border-border bg-secondary/40 grid place-items-center overflow-hidden" data-testid="po-logo-preview">
                            {logoUrl ? <img src={logoUrl} alt="School logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground">No logo</span>}
                        </div>
                        <div className="flex flex-col gap-2">
                            <label>
                                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} data-testid="po-logo-input" />
                                <Button asChild variant="outline" className="rounded-lg" disabled={uploading}>
                                    <span className="cursor-pointer">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> {logoUrl ? "Replace logo" : "Upload logo"}</>}</span>
                                </Button>
                            </label>
                            {logoUrl && <Button variant="ghost" className="text-rose-600 justify-start h-8 px-2" onClick={removeLogo} data-testid="po-logo-remove"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>}
                        </div>
                    </div>
                </div>
                <div>
                    <Label className="text-xs">PO footer</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Appears at the bottom of every purchase order — e.g. payment terms or remittance instructions.</p>
                    <Textarea value={footer} onChange={(e) => setFooter(e.target.value)} rows={4} className="mt-2" placeholder="e.g. Payment terms: Net 30. Remit to Accounts Payable, 45 Academy Lane, Boston, MA 02116." data-testid="po-footer-input" />
                    <div className="mt-3 flex justify-end">
                        <Button disabled={busy} onClick={saveFooter} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="save-po-footer">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save footer</>}
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    );
}

export default function SchoolSettings() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, "school_admin");
    const [tab, setTab] = useState(isAdmin ? "profile" : "sync");

    return (
        <div className="space-y-6 pe-fade-up" data-testid="school-settings">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald flex items-center gap-2">
                    <Settings className="h-6 w-6 text-brand-teal" /> School Settings
                </h1>
                <p className="text-muted-foreground text-sm mt-1">Manage your school profile, Veracross connection, data sync and user roles.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex flex-wrap h-auto">
                    {isAdmin && <TabsTrigger value="profile" data-testid="tab-profile"><Building2 className="h-4 w-4 mr-1.5" /> Profile</TabsTrigger>}
                    {isAdmin && <TabsTrigger value="veracross" data-testid="tab-veracross-setup"><Link2 className="h-4 w-4 mr-1.5" /> Veracross</TabsTrigger>}
                    {isAdmin && <TabsTrigger value="po" data-testid="tab-po"><FileText className="h-4 w-4 mr-1.5" /> Purchase Order</TabsTrigger>}
                    {isAdmin && <TabsTrigger value="email" data-testid="tab-email"><Mail className="h-4 w-4 mr-1.5" /> Email Inbox</TabsTrigger>}
                    <TabsTrigger value="sync" data-testid="tab-sync"><RefreshCw className="h-4 w-4 mr-1.5" /> Sync</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" /> Users & Roles</TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck className="h-4 w-4 mr-1.5" /> Security</TabsTrigger>
                </TabsList>

                {isAdmin && <TabsContent value="profile" className="mt-5"><ProfileTab /></TabsContent>}
                {isAdmin && <TabsContent value="veracross" className="mt-5"><VeracrossTab /></TabsContent>}
                {isAdmin && <TabsContent value="po" className="mt-5"><PurchaseOrderTab /></TabsContent>}
                {isAdmin && <TabsContent value="email" className="mt-5"><EmailInboxTab /></TabsContent>}
                <TabsContent value="sync" className="mt-5"><SyncTab /></TabsContent>
                <TabsContent value="users" className="mt-5"><UsersTab isAdmin={isAdmin} /></TabsContent>
                <TabsContent value="security" className="mt-5"><MfaSecurityCard /></TabsContent>
            </Tabs>
        </div>
    );
}
